import * as React from "react";
import { useState } from "react";
import { ILanguage } from "../../../common/interfaces";
import { allTimeZone } from "../../../timeZone";
import {
	Tooltip,
	Grid,
	Checkbox,
	Box,
	Icon,
	MenuItem,
	Switch,
	Menu,
} from "../../../common/MaterialComponent";
import LoopIcon from "@material-ui/icons/Loop";
import Shared from "../../../common/shared";
import { ButtonPrimary } from "../../../common/sharedStyle";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import BatchMessageCostRequestModel, {
	Group,
	MessageCost,
} from "../../../models/BatchMessage/BatchMessageCostRequestModel";
import BatchMessageLanguageDetailModel from "../../../models/BatchMessage/BatchMessageLanguageDetailModel";
import BatchMessageSchedulingDetailModel, {
	MessageDetail,
	RecurrenceDetail,
} from "../../../models/BatchMessage/BatchMessageSchedulingDetailModel";
import DateTimePicker from "../../DateTimePicker/DateTimePicker";
import LanguageSelection from "../LanguageSelection/LanguageSelection";
import MessageSegmentPreviewDialog from "../MessageSegmentPreviewDialog/MessageSegmentPreviewDialog";
import TimeZoneSelection from "../TimeZoneSelection/TimeZoneSelection";
import "./MessageScheduleRow.css";
import { GetMessageContent } from "../../../hooks/useBatchMessageScheduleConfiguration";
import { GetMessagePreviewContent } from "../../../hooks/useBatchMessagePreviewDetailConfiguration";
import { RecurringMessageSchedule } from "../../../common/enum";
import RecurringSchedulerDialog from "../RecurringSchedulerDialog/RecurringSchedulerDialog";

export interface MessageScheduleRowProps {
	groupIndex?: number;
	groupId?: string;
	messageIndex: number;
	messageDetail: MessageDetail;
	isReadOnly: boolean;
	getIsBatchMessageTypeSMS: boolean;
	getIsBatchMessageTypeWhatsApp: boolean;
	batchMessageCostDetail?: BatchMessageCostRequestModel;
	batchMessageLanguageDetail: BatchMessageLanguageDetailModel | null;
	selectedLanguageList: ILanguage[];
	batchMessageSchedulingDetails?: BatchMessageSchedulingDetailModel[];
	highlightRequiredDetail: boolean;
	africasTalkingEnabled?: boolean;
	country: string;
	currency?: string;
	onMessageSelectionChange: (
		isChecked: boolean,
		messageIndex: number,
		groupIndex?: number
	) => void;
	onLanguageChangeHandler: (
		values: ILanguage[],
		messageIndex: number,
		groupIndex?: number
	) => void;
	onDateTimeValueChange: (
		values: Date | string,
		messageIndex: number,
		groupIndex?: number
	) => void;
	onTimeZoneChange: (
		values: string,
		messageIndex: number,
		groupIndex?: number
	) => void;
	onRecurrenceDetailChange: (
		isRecurring: boolean,
		messageIndex: number,
		recurrenceDetails?: RecurrenceDetail,
		groupIndex?: number
	) => void;
}

export const MessageScheduleRow: React.FC<MessageScheduleRowProps> = ({
	groupIndex,
	groupId,
	messageIndex,
	messageDetail,
	isReadOnly,
	getIsBatchMessageTypeSMS,
	getIsBatchMessageTypeWhatsApp,
	batchMessageCostDetail,
	batchMessageLanguageDetail,
	selectedLanguageList,
	country,
	currency,
	batchMessageSchedulingDetails,
	onMessageSelectionChange,
	onLanguageChangeHandler,
	onDateTimeValueChange,
	onTimeZoneChange,
	onRecurrenceDetailChange,
	highlightRequiredDetail,
	africasTalkingEnabled,
}) => {
	React.useEffect(() => {
		if (getIsBatchMessageTypeWhatsApp) {
			onLanguageChangeHandler(selectedLanguageList, messageIndex, groupIndex);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getIsBatchMessageTypeWhatsApp, groupIndex, messageIndex]);

	const strings = useGetComponentStringsContext().MessageScheduleRow;

	const [
		openMessageSegmentPreviewDialog,
		setOpenMessageSegmentPreviewDialog,
	] = useState<boolean>(false);

	const [
		openRecurringSchedulerDialog,
		setOpenRecurringSchedulerDialog,
	] = useState<boolean>(false);

	const [recurringSchedulerError, setRecurringSchedulerError] = React.useState<
		null | string
	>(null);
	const [isRecurring, setIsRecurring] = React.useState<boolean>(
		messageDetail.isRecurring
	);
	const [isRecurringIndex, setIsRecurringIndex] = React.useState<number>(0);
	const [isRecurringOption, setIsRecurringOption] = React.useState(
		messageDetail.recurrenceDetails
			? messageDetail.recurrenceDetails.option
			: ""
	);
	const [recurringDays, setRecurringDays] = React.useState(
		messageDetail.recurrenceDetails ? messageDetail.recurrenceDetails.days : []
	);
	const [recurringDate, setRecurringDate] = React.useState(
		messageDetail.recurrenceDetails
			? messageDetail.recurrenceDetails.date
			: new Date().toISOString().split("T")[0]
	);
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

	const [messageContent, setMessageContent] = useState("");

	const recurringMessageOptions = [
		{
			key: RecurringMessageSchedule.Daily,
			value: strings.recurringDailyOption,
		},
		{
			key: RecurringMessageSchedule.Weekly,
			value: strings.recurringWeeklyOption,
		},
		{
			key: RecurringMessageSchedule.Monthly,
			value: strings.recurringMonthlyOption,
		},
	];

	React.useEffect(() => {
		if (isRecurring) {
			onRecurrenceDetailChange(
				isRecurring,
				messageIndex,
				new RecurrenceDetail(isRecurringOption, recurringDays, recurringDate),
				groupIndex
			);
		} else {
			onRecurrenceDetailChange(
				isRecurring,
				messageIndex,
				undefined,
				groupIndex
			);
		}

		switch (isRecurringOption) {
			case "Daily":
				setIsRecurringIndex(0);
				break;
			case "Weekly":
				setIsRecurringIndex(1);
				break;
			case "Monthly":
				setIsRecurringIndex(2);
				break;
			default:
				setIsRecurringIndex(0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRecurring]);

	const getMessageCost = (groupId: string, messageTitle?: string) => {
		let cost: number | undefined = 0;
		if (batchMessageCostDetail) {
			const groupIndex = batchMessageCostDetail.groups.findIndex(
				(x: Group) => x.id === groupId
			);
			if (groupIndex !== -1) {
				if (messageTitle !== undefined) {
					cost = batchMessageCostDetail.groups[groupIndex].messageCosts.find(
						(y: MessageCost) => y.title === messageTitle
					)?.cost;
				} else {
					cost = batchMessageCostDetail.groups[groupIndex].costPerGroup;
				}
			}
		}
		return cost !== undefined ? cost : 0;
	};

	const getSelectedLanguageById = (
		selectedLanguageList: string[]
	): ILanguage[] => {
		const languageCodeList = batchMessageLanguageDetail?.languages
			.filter((x) => selectedLanguageList.includes(x.id))
			.map((y) => y.code);
		if (languageCodeList && languageCodeList.length > 0) {
			return Shared.getLanguageByCode(languageCodeList);
		}
		return [];
	};

	const makeRecurringMessage = (event: React.MouseEvent<HTMLElement>) => {
		if (!isRecurring) {
			setAnchorEl(event.currentTarget);
		} else {
			setAnchorEl(null);
			setIsRecurring(!isRecurring);
		}
	};

	const handleRecurringScheduleSelection = (
		event: React.MouseEvent<HTMLElement>,
		index: number
	) => {
		setAnchorEl(null);
		setIsRecurring(!isRecurring);
		setIsRecurringOption(recurringMessageOptions[index].value);
		setIsRecurringIndex(index);
		setOpenRecurringSchedulerDialog(true);
	};

	const previewMessageSegment = (
		messageIndex: number,
		groupIndex: number = -1
	) => {
		let message: string | number = "";
		if (groupId) {
			// used for message schedule configuration
			message = GetMessageContent(
				batchMessageSchedulingDetails!,
				batchMessageLanguageDetail!.languages,
				getIsBatchMessageTypeSMS || getIsBatchMessageTypeWhatsApp,
				messageIndex,
				groupIndex
			);
		} else {
			// used for batch preview detail configuration
			message = GetMessagePreviewContent(
				batchMessageLanguageDetail!.languages,
				!getIsBatchMessageTypeSMS && !getIsBatchMessageTypeWhatsApp,
				messageDetail
			);
		}
		setOpenMessageSegmentPreviewDialog(true);
		setMessageContent(message as string);
	};

	const handleDaySelection = (day: number) => {
		if (recurringDays.includes(day)) {
			setRecurringDays(recurringDays.filter((rd) => rd !== day));
		} else {
			setRecurringDays([...recurringDays, day]);
		}
	};

	const cancelRecurringSchedule = () => {
		setIsRecurring(false);
		setRecurringSchedulerError(null);
		setOpenRecurringSchedulerDialog(false);
	};

	const closePreviewSegmentDialog = () => {
		setOpenMessageSegmentPreviewDialog(false);
	};

	const closeDialog = () => {
		setOpenRecurringSchedulerDialog(false);
	};

	const confirmDialog = () => {
		switch (isRecurringIndex) {
			case 0:
				setRecurringSchedulerError(null);
				setOpenRecurringSchedulerDialog(false);
				updateRecurrenceSchedule();
				break;
			case 1:
				if (recurringDays.length === 0) {
					setRecurringSchedulerError(strings.weeklyRecurringErrorNoDays);
				} else {
					setOpenRecurringSchedulerDialog(false);
					setRecurringSchedulerError(null);
					updateRecurrenceSchedule();
				}
				break;
			case 2:
				if (
					parseInt(recurringDate.substr(5, 2)) <
					parseInt(new Date().toISOString().split("T")[0].substr(5, 2))
				) {
					setRecurringSchedulerError(strings.monthlyRecurringErrorBeforeMonth);
				} else {
					setOpenRecurringSchedulerDialog(false);
					setRecurringSchedulerError(null);
					updateRecurrenceSchedule();
				}
				break;
		}
	};

	const updateRecurrenceSchedule = () => {
		onRecurrenceDetailChange(
			isRecurring,
			messageIndex,
			new RecurrenceDetail(isRecurringOption, recurringDays, recurringDate),
			groupIndex
		);
	};

	return (
		<>
			<Grid
				container
				spacing={2}
				key={messageIndex}
				data-testid="messageScheduleRowContainer"
			>
				<Grid item xs={1} className="chkBox">
					<Checkbox
						color="primary"
						checked={messageDetail.isSelected}
						disabled={isReadOnly}
						data-testid="selectMessageCheckbox"
						onChange={(event) =>
							onMessageSelectionChange(
								event.target.checked,
								messageIndex,
								groupIndex
							)
						}
					/>
				</Grid>
				<Grid
					item
					xs={
						groupId
							? 1
							: getIsBatchMessageTypeSMS || getIsBatchMessageTypeWhatsApp
							? 1
							: 2
					}
				>
					<div className="grpMessageTitle">{messageDetail.title}</div>
				</Grid>
				<Grid item sm={4} md={3}>
					{!getIsBatchMessageTypeWhatsApp && (
						<LanguageSelection
							onLanguageChange={(values: ILanguage[]) =>
								onLanguageChangeHandler(values, messageIndex, groupIndex)
							}
							languageList={selectedLanguageList}
							selectedLanguages={getSelectedLanguageById(
								messageDetail.languageIds
							)}
							isReadOnly={isReadOnly}
							requiresValidation={highlightRequiredDetail}
						/>
					)}
				</Grid>
				<Grid item xs={3} className="messageScheduleControl">
					<DateTimePicker
						dateTime={messageDetail.dateTime}
						minDateTime={new Date()}
						onDateTimeValueChange={(value: Date | string) => {
							onDateTimeValueChange(value, messageIndex, groupIndex);
						}}
						isReadOnly={isReadOnly}
						requiresValidation={highlightRequiredDetail}
					/>
				</Grid>
				<Grid item xs={3} className="messageScheduleControl">
					<TimeZoneSelection
						timeZoneList={allTimeZone}
						selectedTimeZone={messageDetail.timezone}
						onTimeZoneChange={(value: string) => {
							onTimeZoneChange(value, messageIndex, groupIndex);
						}}
						isReadOnly={isReadOnly}
						requiresValidation={highlightRequiredDetail}
					></TimeZoneSelection>
				</Grid>
				<Grid item sm={2} md={1} className="recurringControls">
					<>
						<div>
							<LoopIcon />
						</div>
						<Tooltip title={strings.makeRecurringToolTip}>
							<div>
								<Switch
									checked={isRecurring}
									name="makeRecurring"
									color="primary"
									onClick={makeRecurringMessage}
									disabled={isReadOnly}
								/>
							</div>
						</Tooltip>
						{isRecurring && (
							<div>
								<Tooltip title={strings.viewRecurringSchedule}>
									<span>
										<ButtonPrimary
											variant="contained"
											color="primary"
											data-testid="recurringSchedulePreviewBtn"
											onClick={() => setOpenRecurringSchedulerDialog(true)}
											className="grpActionBtn"
										>
											{`${strings.recurringWhen} ${isRecurringOption}`}
										</ButtonPrimary>
									</span>
								</Tooltip>
							</div>
						)}
						<Menu
							anchorEl={anchorEl}
							keepMounted
							open={Boolean(anchorEl)}
							getContentAnchorEl={null}
							anchorOrigin={{
								vertical: "bottom",
								horizontal: "center",
							}}
							onClose={() => {
								setAnchorEl(null);
							}}
							data-testid="recurringMessageScheduleList"
						>
							{recurringMessageOptions.map((option, index) => (
								<MenuItem
									key={index}
									onClick={(event) =>
										handleRecurringScheduleSelection(event, index)
									}
									data-testid="recurringMessageScheduleListItem"
								>
									{option.value}
								</MenuItem>
							))}
						</Menu>
					</>
				</Grid>
				<Grid item md={1} className="textCenter">
					<Box pt={1}>
						{(getIsBatchMessageTypeSMS || getIsBatchMessageTypeWhatsApp) && (
							<Tooltip title={strings.previewMessageSegmentToolTip}>
								<span>
									<ButtonPrimary
										variant="contained"
										color="primary"
										data-testid="messageSegmentPreviewBtn"
										onClick={() =>
											previewMessageSegment(messageIndex, groupIndex)
										}
										className="grpActionBtn"
										disabled={messageDetail.languageIds.length === 0}
									>
										<Icon>segment</Icon>
									</ButtonPrimary>
								</span>
							</Tooltip>
						)}
						{groupId && !getIsBatchMessageTypeWhatsApp && (
							<Tooltip title={strings.approximateMessageCostToolTip}>
								<span>
									<b data-testid="messageCostId">
										{africasTalkingEnabled
											? "N/A"
											: Shared.getFormatedCurrencyValue(
													messageDetail.cost
														? messageDetail.cost
														: getMessageCost(groupId, messageDetail.title),
													currency
											  )}
									</b>
								</span>
							</Tooltip>
						)}
					</Box>
				</Grid>
			</Grid>

			{openRecurringSchedulerDialog && (
				<RecurringSchedulerDialog
					open={openRecurringSchedulerDialog}
					isRecurringOption={isRecurringOption}
					recurringDays={recurringDays}
					handleDaySelection={handleDaySelection}
					index={isRecurringIndex}
					confirmDialog={confirmDialog}
					cancelDialog={cancelRecurringSchedule}
					dateTime={messageDetail.dateTime}
					recurringDate={recurringDate}
					setRecurringDate={setRecurringDate}
					recurringSchedulerError={recurringSchedulerError}
					isReadOnly={isReadOnly}
					closeDialog={closeDialog}
				/>
			)}

			{openMessageSegmentPreviewDialog && (
				<MessageSegmentPreviewDialog
					open={openMessageSegmentPreviewDialog}
					message={messageContent}
					index=""
					country={country}
					closeDialog={closePreviewSegmentDialog}
				></MessageSegmentPreviewDialog>
			)}
		</>
	);
};
